import React, { useState } from 'react';

interface BulkActionsPanelProps {
  selectedCount: number;
  onBulkAccept: (notes?: string) => void;
  onBulkReject: (notes?: string) => void;
  onClearSelection: () => void;
}

export const BulkActionsPanel: React.FC<BulkActionsPanelProps> = ({
  selectedCount,
  onBulkAccept,
  onBulkReject,
  onClearSelection
}) => {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [pendingAction, setPendingAction] = useState<'accept' | 'reject' | null>(null);

  const handleBulkAction = (action: 'accept' | 'reject') => {
    setPendingAction(action);
    setShowNotesModal(true);
  };

  const confirmAction = () => {
    if (pendingAction === 'accept') {
      onBulkAccept(notes || undefined);
    } else if (pendingAction === 'reject') {
      onBulkReject(notes || undefined);
    }
    
    setShowNotesModal(false);
    setNotes('');
    setPendingAction(null);
  };

  const cancelAction = () => {
    setShowNotesModal(false);
    setNotes('');
    setPendingAction(null);
  };

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {selectedCount} change{selectedCount !== 1 ? 's' : ''} selected
              </h3>
              <p className="text-sm text-blue-700">
                Choose an action to apply to all selected changes.
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleBulkAction('accept')}
              className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Accept All
            </button>
            <button
              onClick={() => handleBulkAction('reject')}
              className="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Reject All
            </button>
            <button
              onClick={onClearSelection}
              className="px-3 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {pendingAction === 'accept' ? 'Accept' : 'Reject'} {selectedCount} Changes
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Add a note explaining why you ${pendingAction}ed these changes...`}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={cancelAction}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 ${
                    pendingAction === 'accept'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {pendingAction === 'accept' ? 'Accept' : 'Reject'} Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};